<template>
  <div class="ea-chatbot right" :class="{ open: isOpen }" aria-live="polite">
    <!-- Pestaña / botón lateral derecho -->
    <button class="ea-tab" @click="toggle" :aria-expanded="isOpen">
      <span v-if="lang==='ES'">Asistente</span>
      <span v-else>Assistant</span>
    </button>

    <!-- Panel del chat -->
    <section class="ea-panel" v-show="isOpen">
      <div class="ea-blob" aria-hidden="true"></div>
      <header class="ea-header">
        <div class="ea-title">
          <strong v-if="lang==='ES'">Asistente EA Magic</strong>
          <strong v-else>EA Magic Assistant</strong>
          <small>{{ company.name }}</small>
        </div>
        <button class="ea-close" @click="isOpen=false" :aria-label="lang==='ES' ? 'Cerrar' : 'Close'">×</button>
      </header>

      <!-- Sugerencias / Quick chips -->
      <div class="ea-suggest">
        <button
          v-for="(s, i) in suggestions"
          :key="i"
          class="chip"
          @click="quickAsk(s)"
          :title="s"
        >{{ s }}</button>
      </div>

      <div class="ea-body" ref="scrollBox">
        <div v-for="(m,idx) in messages" :key="idx" class="ea-msg" :class="m.role">
          <div class="ea-bubble" v-html="m.html"></div>
        </div>
        <div v-if="typing" class="ea-msg bot">
          <div class="ea-bubble">
            <span class="dots"><i></i><i></i><i></i></span>
          </div>
        </div>
      </div>

      <!-- FAQs de un toque -->
      <div class="ea-faqs">
        <button v-for="(q, i) in faqs" :key="'faq-'+i" class="faq" @click="quickAsk(q)">{{ q }}</button>
      </div>

      <footer class="ea-footer">
        <input
          v-model="draft"
          :placeholder="lang==='ES' ? 'Escribe tu pregunta...' : 'Type your question...'"
          @keydown.enter.prevent="handleSend"
          class="ea-input"
        />
        <button class="ea-send" @click="handleSend" :aria-label="lang==='ES' ? 'Enviar' : 'Send'">➤</button>
      </footer>
    </section>
  </div>
</template>

<script>
export default {
  name: 'ChatBotEA',
  props: {
    lang: { type: String, default: 'ES' }, // 'ES' | 'EN'
    company: {
      type: Object,
      default: () => ({
        name: 'EA Magic Cleaning LLC',
        phone: '(612) 261-9287',
        phoneHref: 'tel:+16122619287',
        email: 'eamagiccleaning@gmail.com',
        address: '11345 Deer Ridge Ln, Hopkins, MN 55343',
        maps: 'https://www.google.com/maps?q=11345%20Deer%20Ridge%20Ln%2C%20Hopkins%2C%20MN%2055343',
        wa: 'sms:+16122619287&body=Hola, me interesa información sobre limpieza'
      })
    },
    socials: {
      type: Object,
      default: () => ({
        facebook: 'https://www.facebook.com/share/1EFrEHeDam/?mibextid=wwXIfr',
        tiktok: null // agrega tu enlace si lo tienes
      })
    },
    // Lista de servicios (se muestra en /servicios y para respuestas)
    services: {
      type: Array,
      default: () => ([
        'Limpieza de casas / House cleaning',
        'Limpieza de apartamentos / Apartment cleaning',
        'Limpieza post-construcción / Post-construction cleaning',
        'Limpieza en mudanzas / Move-in/Move-out',
        'Limpieza profunda / Deep cleaning',
        'Limpieza regular / Regular cleaning',
        'Limpieza de oficinas / Office cleaning',
        'Limpieza de iglesias / Church cleaning'
      ])
    },
    hours: {
      type: Object,
      default: () => ({
        ES: 'Lun–Sáb 8:00 a 18:00. Domingos bajo cita.',
        EN: 'Mon–Sat 8:00 AM – 6:00 PM. Sundays by appointment.'
      })
    },
    coverage: {
      type: Object,
      default: () => ({
        ES: 'Atendemos Hopkins, Minneapolis y alrededores en Minnesota.',
        EN: 'We serve Hopkins, Minneapolis, and surrounding areas in Minnesota.'
      })
    }
  },
  data() {
    return {
      isOpen: true, // 👈 se abre automáticamente
      draft: '',
      messages: [],
      typing: false
    }
  },
  computed: {
    suggestions(){
      return this.lang==='ES'
        ? ['¿Cómo limpian casas?', 'Precios', 'Cobertura', 'Horario', 'Reservar', 'Redes sociales']
        : ['How do you clean houses?', 'Pricing', 'Coverage', 'Hours', 'Book', 'Social media'];
    },
    faqs(){
      return this.lang==='ES'
        ? [
            '¿Qué incluye limpieza profunda?',
            '¿Usan productos eco-friendly?',
            '¿Traen equipo y suministros?',
            'Formas de pago',
            '¿Garantía y reembolso?',
            'Reprogramar o cancelar',
            '¿Tienen factura/recibo?',
            '¿Están asegurados y con antecedentes revisados?',
            '¿Ofrecen limpieza post-construcción?',
            '¿Oficinas e iglesias?',
            '¿Descuentos por servicio regular?',
            '¿Cuánto dura una limpieza?'
          ]
        : [
            'What does deep cleaning include?',
            'Do you use eco-friendly products?',
            'Do you bring supplies and equipment?',
            'Pet & kid friendly?',
            'Payment methods',
            'Satisfaction & refunds?',
            'Reschedule or cancel',
            'Invoice/receipt?',
            'Insured & background-checked?',
            'Post-construction cleaning?',
            'Offices & churches?',
            'Recurring discounts?',
            'How long does a cleaning take?'
          ]
    }
  },
  mounted() {
    // Bienvenida + micro-anim para sorpresa
    const helloES = `✨ Bienvenido/a al asistente de <strong>${this.company.name}</strong>.<br>
Puedo ayudarte con <em>servicios, precios, cobertura, horarios, contacto</em> y más.<br>
Escribe tu pregunta o toca un botón.`;
    const helloEN = `✨ Welcome to <strong>${this.company.name}</strong> assistant.<br>
I can help with <em>services, pricing, coverage, hours, contact</em> and more.<br>
Type your question or tap a button.`;
    this.pushBot(this.lang==='ES' ? helloES : helloEN);
  },
  methods: {
    toggle(){ this.isOpen = !this.isOpen; this.$nextTick(this.scrollToEnd) },
    scrollToEnd(){
      const el = this.$refs.scrollBox;
      if (el) el.scrollTop = el.scrollHeight;
    },
    quickAsk(text){
      this.draft = text;
      this.handleSend();
    },
    handleSend() {
      const text = this.draft.trim();
      if (!text) return;
      this.pushUser(this.escape(text));
      this.draft = '';
      this.typing = true;
      setTimeout(() => {
        const replyHtml = this.route(text);
        this.pushBot(replyHtml);
        this.typing = false;
        this.$nextTick(this.scrollToEnd);
      }, 420);
    },
    pushUser(html){ this.messages.push({ role: 'user', html }) ; this.$nextTick(this.scrollToEnd) },
    pushBot(html){ this.messages.push({ role: 'bot', html }) ; this.$nextTick(this.scrollToEnd) },
    escape(s){ return s.replace(/[<>&"]/g, ch => ({'<':'&lt;','>':'&gt;','&':'&amp;','"':'&quot;'}[ch])) },

    // --- Router por palabras clave (ES/EN) con SMS ---
    route(qRaw){
      const q = qRaw.toLowerCase();
      const ES = this.lang === 'ES';

      const linkPhone = `<a href="${this.company.phoneHref}" class="ea-inline">${this.company.phone}</a>`;
      const linkMail  = `<a href="mailto:${this.company.email}" class="ea-inline">${this.company.email}</a>`;
      const linkSMS   = `<a href="${this.company.wa}" target="_blank" class="ea-btn">SMS</a>`;
      const linkMaps  = `<a href="${this.company.maps}" target="_blank" class="ea-inline">Google Maps</a>`;

      const fb = this.socials.facebook ? `<a href="${this.socials.facebook}" class="ea-inline" target="_blank">Facebook</a>` : '';
      const tk = this.socials.tiktok   ? `<a href="${this.socials.tiktok}" class="ea-inline" target="_blank">TikTok</a>` : '';

      // ---- Servicios genéricos
      if (/(servicio|servicios|service|services|qué hacen|que hacen|what do you do)/.test(q)) {
        const list = this.services.map(s => `<li>${this.escape(s)}</li>`).join('');
        return ES
          ? `<p>Ofrecemos:</p><ul>${list}</ul><p>¿Te agendo una limpieza? ${linkSMS}</p>`
          : `<p>We offer:</p><ul>${list}</ul><p>Want to book a cleaning? ${linkSMS}</p>`;
      }

      // ---- Servicio: Limpieza de casas
      if (/(casa|casas|house\b|home\b).*(limpia|limpieza|clean)/.test(q) || /(cómo limpian casas|como limpian casas|how do you clean houses)/.test(q)) {
        return ES
          ? `<p><strong>Limpieza de casas</strong> incluye: polvo en superficies y zócalos, aspirado/trapeado, cocina (superficies, fregadero, exterior de electrodomésticos), baños (inodoro, lavamanos, regadera, espejos), recámaras/salas (hacer camas, ordenar).<br>
Opcional: interior de horno/refri, interiores de gabinetes, ventanas interiores.<br>
Suministros y equipo incluidos. Amigables con mascotas y niños. ${linkSMS}</p>`
          : `<p><strong>House cleaning</strong> includes: dusting surfaces/baseboards, vacuum & mop, kitchen (surfaces, sink, appliance exteriors), bathrooms (toilet, sink, shower, mirrors), bedrooms/living (make beds, tidy).<br>
Optional: inside oven/fridge, inside cabinets, interior windows.<br>
We bring supplies & equipment. Pet- and kid-friendly. ${linkSMS}</p>`;
      }

      // ---- Limpieza profunda
      if (/(profunda|deep)\s*(clean|cleaning)?/.test(q)) {
        return ES
          ? `<p><strong>Limpieza profunda</strong>: detalles detrás y debajo de muebles, juntas/boquillas, puertas/marcos, lámparas, ventilas, azulejos, zócalos a fondo, desinfección focalizada. Ideal primera visita o espacios con tiempo sin limpiar. ${linkSMS}</p>`
          : `<p><strong>Deep cleaning</strong>: behind/under furniture, grout/tiles, doors/frames, light fixtures, vents, detailed baseboards, targeted disinfection. Great for first-time or long-interval cleanings. ${linkSMS}</p>`;
      }

      // ---- Apartamentos
      if (/(apartamento|departamento|apartment)/.test(q)) {
        return ES
          ? `<p><strong>Apartamentos</strong>: enfoque en espacios compactos y superficies de alto contacto; cocina y baño prioritarios. Opciones de mantenimiento semanal/quincenal. ${linkSMS}</p>`
          : `<p><strong>Apartments</strong>: compact spaces with focus on high-touch surfaces; kitchen & bathroom priority. Weekly/bi-weekly maintenance available. ${linkSMS}</p>`;
      }

      // ---- Mudanzas (entrada/salida)
      if (/(mudanza|move[- ]?in|move[- ]?out|entrada|salida)/.test(q)) {
        return ES
          ? `<p><strong>Move-in/Move-out</strong>: interiores de gabinetes, cajones, closets, limpieza detallada de cocina y baños, baseboards, puertas, puntos de contacto, y revisión final. ${linkSMS}</p>`
          : `<p><strong>Move-in/Move-out</strong>: inside cabinets/drawers/closets, detailed kitchen & bath, baseboards, doors, touch points, and final walkthrough. ${linkSMS}</p>`;
      }

      // ---- Post-construcción
      if (/(post|post[- ]?)?construcci(ón|on)|post[- ]?construction/.test(q)) {
        return ES
          ? `<p><strong>Post-construcción</strong>: remoción de polvo fino, restos de obra, limpieza de zócalos/ventanas/interiores de gabinetes si aplica; se puede requerir 2 pasadas por polvo residual. ${linkSMS}</p>`
          : `<p><strong>Post-construction</strong>: removal of fine dust, debris pickup, baseboards/windows/inside cabinets as needed; 2 passes may be required due to residual dust. ${linkSMS}</p>`;
      }

      // ---- Oficinas
      if (/(oficina|office)/.test(q)) {
        return ES
          ? `<p><strong>Oficinas</strong>: escritorios (superficies despejadas), salas de juntas, áreas comunes, baños, cocina/coffee station, vaciado de botes, desinfección de puntos de contacto. Horarios fuera de oficina disponibles. ${linkSMS}</p>`
          : `<p><strong>Offices</strong>: desks (cleared areas), meeting rooms, common areas, restrooms, kitchenette/coffee station, trash removal, touch-point disinfection. After-hours available. ${linkSMS}</p>`;
      }

      // ---- Iglesias
      if (/(iglesia|church)/.test(q)) {
        return ES
          ? `<p><strong>Iglesias</strong>: respeto de santuarios, bancas/pasillos, áreas comunes, limpieza cuidadosa de superficies delicadas y protocolos específicos del templo. ${linkSMS}</p>`
          : `<p><strong>Churches</strong>: respectful of sanctuaries, pews/aisles, common areas, careful handling of delicate surfaces and site protocols. ${linkSMS}</p>`;
      }

      // ---- Regular / suscripción
      if (/(regular|semanal|quincenal|mensual|weekly|bi[- ]?weekly|monthly)/.test(q)) {
        return ES
          ? `<p><strong>Limpieza regular</strong>: planes semanal/quincenal/mensual con tarifas preferenciales tras la primera limpieza profunda si aplica. ${linkSMS}</p>`
          : `<p><strong>Recurring cleaning</strong>: weekly/bi-weekly/monthly plans with preferred rates after initial deep clean if needed. ${linkSMS}</p>`;
      }

      // ---- Precios
      if (/(precio|precios|cost|price|how much|cuánto)/.test(q)) {
        return ES
          ? `<p>El precio depende de tamaño, estado y tipo de servicio. Podemos cotizar rápido por SMS con algunas fotos o descripción. ${linkSMS}</p>`
          : `<p>Pricing depends on size, condition, and service type. We can quote quickly via SMS with a few photos or details. ${linkSMS}</p>`;
      }

      // ---- Duración
      if (/(dur(a|á)ci(ó|o)n|cu[aá]nto tarda|how long|duration|time)/.test(q)) {
        return ES
          ? `<p>La duración varía por metraje y detalles. Referencia: hogar promedio 2–4 h (2 personas). Limpieza profunda o post-construcción puede requerir más tiempo. ${linkSMS}</p>`
          : `<p>Timing varies by size and scope. As a reference: average home 2–4 h (2 cleaners). Deep or post-construction may take longer. ${linkSMS}</p>`;
      }

      // ---- Cobertura / ubicación
      if (/(zona|cobertura|área|coverage|where|location|minnesota|hopkins|minneapolis|address|direcci(ó|o)n)/.test(q)) {
        return ES
          ? `<p>${this.coverage.ES} Ver ubicación: ${linkMaps}</p>`
          : `<p>${this.coverage.EN} See location: ${linkMaps}</p>`;
      }

      // ---- Horarios
      if (/(horario|horarios|hours|open|opening|schedule)/.test(q)) {
        return ES ? `<p>${this.hours.ES}</p>` : `<p>${this.hours.EN}</p>`;
      }

      // ---- Contacto
      if (/(contact|contacto|phone|tel|email|mail|llamar|call)/.test(q)) {
        return ES
          ? `<p>Contáctanos: ${linkPhone} · ${linkMail} · ${linkSMS}</p>`
          : `<p>Contact us: ${linkPhone} · ${linkMail} · ${linkSMS}</p>`;
      }

      // ---- Redes sociales
      if (/(red(es)?|social(es)?|facebook|tik ?tok|tiktok|instagram)/.test(q)) {
        const none = ES ? 'Pronto compartiremos más redes.' : 'We’ll share more socials soon.';
        const parts = [fb, tk].filter(Boolean).join(' · ');
        return ES
          ? `<p>¡Sí! Síguenos en ${parts || none}</p>`
          : `<p>Yes! Follow us on ${parts || none}</p>`;
      }

      // ---- Pagos
      if (/(pago|pagos|pay|payment|tarjeta|efectivo|cash|card|zelle|check|cheque)/.test(q)) {
        return ES
          ? `<p><strong>Pagos</strong>: aceptamos efectivo y opciones electrónicas comunes (consulta disponibilidad). Se puede emitir recibo/factura digital.</p>`
          : `<p><strong>Payments</strong>: we accept cash and common electronic options (ask for availability). Digital receipt/invoice available.</p>`;
      }

      // ---- Garantía / satisfacción
      if (/(garant(í|i)a|satisfacci(ó|o)n|refund|reembolso|warranty)/.test(q)) {
        return ES
          ? `<p><strong>Garantía de satisfacción</strong>: si algo no queda como esperabas, avísanos dentro de 24h y regresamos a corregir áreas acordadas.</p>`
          : `<p><strong>Satisfaction guarantee</strong>: if something isn’t as expected, tell us within 24h and we’ll return to fix agreed areas.</p>`;
      }

      // ---- Reprogramar / cancelar
      if (/(cancel(ar)?|reprogram(ar)?|reschedul|cancel)/.test(q)) {
        return ES
          ? `<p>Puedes reprogramar o cancelar con 24h de anticipación sin cargo. Para cambios urgentes, escríbenos por ${linkSMS}.</p>`
          : `<p>You can reschedule or cancel with 24h notice at no charge. For urgent changes, message us via ${linkSMS}.</p>`;
      }

      // ---- Supplies / eco-friendly / equipo
      if (/(eco|verde|green|eco[- ]?friendly|suministros|supplies|equipo|equipment|hepa|productos)/.test(q)) {
        return ES
          ? `<p>Traemos <strong>suministros y equipo</strong> propios (aspiradora HEPA si se requiere). Podemos usar <strong>productos eco-friendly</strong> y libres de fragancias fuertes si lo solicitas. ${linkSMS}</p>`
          : `<p>We bring our own <strong>supplies & equipment</strong> (HEPA vacuum upon request). We can use <strong>eco-friendly</strong> and low-fragrance products if you prefer. ${linkSMS}</p>`;
      }

      // ---- Factura / recibo
      if (/(factura|recibo|invoice|receipt)/.test(q)) {
        return ES
          ? `<p>Podemos emitir <strong>recibo/factura digital</strong> después del servicio o de forma recurrente para oficinas.</p>`
          : `<p>We can provide a <strong>digital receipt/invoice</strong> after service or on a recurring basis for offices.</p>`;
      }

      // ---- Seguro / background
      if (/(asegurad[oa]s?|insured|bonded|background|antecedentes)/.test(q)) {
        return ES
          ? `<p>Trabajamos con <strong>personal de confianza</strong>. Podemos compartir comprobantes bajo solicitud para contratos comerciales.</p>`
          : `<p>We work with <strong>trusted staff</strong>. We can share documentation upon request for commercial contracts.</p>`;
      }

      // ---- Reservar
      if (/(book|reservar|agenda|schedule)/.test(q)) {
        return ES
          ? `<p>¡Perfecto! Te ayudo por SMS para confirmar fecha y servicio. ${linkSMS}</p>`
          : `<p>Great! I’ll help you via SMS to confirm date and service. ${linkSMS}</p>`;
      }

      // ---- Fallback
      return ES
        ? `<p>No estoy seguro de haber entendido. Puedo ayudarte con <em>servicios, precios, horarios, cobertura, contacto, pagos, garantías</em> y más. ¿Me das un poco más de detalle?</p>`
        : `<p>I’m not sure I understood. I can help with <em>services, pricing, hours, coverage, contact, payments, guarantees</em> and more. Could you share a bit more detail?</p>`;
    }
  }
}
</script>

<style scoped>
/* ====== Layout base, lado derecho ====== */
.ea-chatbot { position: fixed; bottom: 24px; z-index: 9999; }
.ea-chatbot.right { right: 0; } /* 👈 anclado a la derecha */
.ea-tab {
  position: absolute; right: 8px; bottom: 0;
  background: linear-gradient(135deg, #0ea5e9, #8b5cf6 60%, #eab308);
  color: #fff; border: 0;
  padding: 10px 14px; border-radius: 12px;
  box-shadow: 0 12px 28px rgba(0,0,0,.25);
  cursor: pointer; font-weight: 700; letter-spacing:.2px;
  backdrop-filter: blur(6px);
  transition: transform .2s ease, box-shadow .2s ease, filter .2s ease;
}
.ea-tab:hover { transform: translateY(-2px); box-shadow: 0 16px 36px rgba(0,0,0,.3); }

.ea-panel {
  width: min(360px, 92vw); height: 520px;
  background: rgba(255,255,255,.9); border-radius: 16px;
  border: 1px solid rgba(255,255,255,.5);
  box-shadow: 0 30px 60px rgba(2,6,23,.30), 0 0 0 1px rgba(14,23,42,.04) inset;
  overflow: hidden; margin-right: 8px; margin-bottom: 50px;
  display: flex; flex-direction: column;
  backdrop-filter: blur(12px);
  animation: pop .45s ease both;
  position: relative;
}

/* Fondo moderno (blob) */
.ea-blob {
  position: absolute; inset: -40% -30% auto auto; height: 260px; width: 260px;
  background: radial-gradient(closest-side, rgba(139,92,246,.35), rgba(14,165,233,.25), transparent 70%);
  filter: blur(18px);
  animation: float 6s ease-in-out infinite alternate;
  pointer-events: none;
}

/* Header */
.ea-header {
  padding: 12px 14px; background: linear-gradient(135deg, #0f172a, #111827);
  color: #fff; display: flex; align-items: center; justify-content: space-between;
  border-bottom: 1px solid rgba(255,255,255,.08);
}
.ea-title small { display:block; opacity:.8; font-weight:400; }
.ea-close { background: transparent; color: #fff; border: 0; font-size: 22px; cursor: pointer; }

/* Sugerencias */
.ea-suggest { display:flex; flex-wrap: wrap; gap: 8px; padding: 10px 12px 0; }
.chip {
  border:1px solid rgba(2,6,23,.12); border-radius: 999px; padding:6px 10px;
  background: rgba(255,255,255,.7); backdrop-filter: blur(8px);
  font-size: 12px; cursor: pointer; transition: transform .15s ease, box-shadow .15s ease;
}
.chip:hover { transform: translateY(-1px); box-shadow: 0 6px 16px rgba(0,0,0,.08); }

/* Body */
.ea-body {
  flex: 1; padding: 12px; overflow: auto; background: linear-gradient(180deg, #f8fafc, #f1f5f9);
}

/* FAQs */
.ea-faqs { display:flex; gap: 6px; padding: 6px 12px; overflow-x:auto; }
.faq {
  white-space: nowrap; font-size: 12px; padding: 6px 10px; border-radius: 10px;
  background:#0f172a; color:#fff; border:0; cursor:pointer;
  opacity:.95; transition: opacity .2s ease, transform .2s ease;
}
.faq:hover { opacity:1; transform: translateY(-1px); }

/* Footer */
.ea-footer { display: flex; gap: 8px; padding: 10px; border-top: 1px solid #e2e8f0; background: rgba(255,255,255,.8); }
.ea-input {
  flex: 1; border: 1px solid #cbd5e1; border-radius: 12px; padding: 11px 12px;
}
.ea-send {
  background: linear-gradient(135deg, #0ea5e9, #8b5cf6);
  color: #fff; border: 0; border-radius: 12px; padding: 0 14px; cursor: pointer; font-weight:700;
  box-shadow: 0 8px 18px rgba(139,92,246,.35);
}

/* Mensajes */
.ea-msg { display:flex; margin: 8px 0; }
.ea-msg.user { justify-content: flex-end; }
.ea-bubble {
  max-width: 85%; padding: 10px 12px; border-radius: 12px; line-height: 1.45;
  background: #fff; border: 1px solid #e2e8f0;
  animation: fadeIn .2s ease both;
}
.ea-msg.user .ea-bubble {
  background: linear-gradient(135deg, #0ea5e9, #8b5cf6);
  color: #fff; border-color: transparent;
}
.ea-inline { color:#2563eb; text-decoration: underline; }
.ea-btn {
  display:inline-block; padding: 6px 10px; border-radius: 10px; border:1px solid #0f172a;
  text-decoration:none; color:#0f172a; font-weight:700; background:#fff;
}

/* Typing dots */
.dots { display:inline-flex; gap:4px; }
.dots i { width:6px; height:6px; border-radius:50%; background:#94a3b8; display:block; animation: b 1s infinite ease-in-out; }
.dots i:nth-child(2){ animation-delay: .15s } .dots i:nth-child(3){ animation-delay: .3s }

/* Animaciones */
@keyframes b { 0%,80%,100%{ transform: translateY(0)} 40%{ transform: translateY(-4px)} }
@keyframes pop { from{ transform: translateY(10px) scale(.98); opacity:0 } to{ transform: translateY(0) scale(1); opacity:1 } }
@keyframes float { from{ transform: translate(0,0)} to{ transform: translate(-10px,10px)} }

@media (max-width: 480px){
  .ea-panel { height: 70vh; }
}
</style>
